<template>
  <div class="o-main-content-area report-page">
    <div class="report-page__header">
      <h4 class="report-page__title">Send Onboarding Link</h4>
      <p class="report-page__sub">
        Insert a phone number into the dispatch flow. We validate the
        number, create a stub worker record, and dispatch a single-use
        SMS (and optional email) link with the TTL set on the chosen
        profile.
      </p>
    </div>

    <form class="send-form" @submit.prevent="onSubmit">
      <label>Profile
        <select v-model="form.profile_id" required>
          <option disabled value="">— select a profile —</option>
          <option v-for="p in profiles" :key="p.id" :value="p.id">
            {{ p.profile_name }}<span v-if="p.is_default"> (default)</span>
          </option>
        </select>
      </label>

      <!-- #1481 — summary of the fixed elements of the selected template,
           plus its QR code (multi-use public onboarding entry). The QR
           encodes the profile's cs3.me / launch URL; scanning it starts
           the same onboarding flow without a dispatched per-worker link. -->
      <div v-if="selectedProfile" class="o-template-summary">
        <h4 class="o-template-summary__title">
          <i class="fa fa-info-circle" aria-hidden="true"></i>
          Template: {{ selectedProfile.profile_name }}
        </h4>
        <p class="o-template-summary__hint">These values are preset by this template and applied to the worker automatically:</p>
        <ul class="o-template-summary__list">
          <li v-if="selectedSkills"><strong>Skills:</strong> {{ selectedSkills }}</li>
          <li v-if="selectedProfile.local_status"><strong>Status / List:</strong> {{ selectedProfile.local_status }}</li>
          <li v-if="selectedProfile.seniority !== null && selectedProfile.seniority !== undefined && selectedProfile.seniority !== ''"><strong>Seniority:</strong> {{ selectedProfile.seniority }}</li>
          <li v-if="selectedProfile.member_since"><strong>Member since:</strong> {{ selectedProfile.member_since }}</li>
          <li v-if="selectedProfile.date_of_hire"><strong>Date of hire:</strong> {{ selectedProfile.date_of_hire }}</li>
          <li v-if="selectedProfile.photo_mode === 'required'"><strong>Photo:</strong> required</li>
        </ul>

        <div class="o-template-qr">
          <div class="o-template-qr__label">Scan to onboard (QR)</div>
          <img v-if="qrUrl" :src="qrUrl" alt="Onboarding QR code" class="o-template-qr__img" />
          <div v-else-if="qrLoading" class="o-template-qr__msg">Generating QR…</div>
          <div v-else-if="qrError" class="o-template-qr__msg">{{ qrError }}</div>
          <a v-if="launchUrl" :href="launchUrl" target="_blank" rel="noopener" class="o-template-qr__url">{{ launchUrl }}</a>
        </div>
      </div>

      <label>Phone (US numbers auto-prefixed +1)
        <input v-model="form.phone" placeholder="(555) 123-4567" required>
      </label>

      <label>Email (optional)
        <input v-model="form.email" type="email" placeholder="worker@example.com">
      </label>

      <label>First name (optional, pre-fills the form)
        <input v-model="form.first_name" maxlength="80">
      </label>
      <label>Last name (optional)
        <input v-model="form.last_name" maxlength="80">
      </label>

      <button type="submit" class="btn btn-primary" :disabled="sending">
        {{ sending ? 'Sending…' : 'Send link' }}
      </button>

      <p v-if="error" class="report-error">{{ error }}</p>
    </form>

    <div v-if="result" class="result-card">
      <h5>Link sent</h5>
      <ul>
        <li><strong>Worker stub created:</strong> <code>{{ result.employee_id }}</code></li>
        <li><strong>Token:</strong> <code>{{ result.token }}</code></li>
        <li><strong>Expires:</strong> {{ formatTime(result.expires_at) }}</li>
        <li v-if="result.short_url"><strong>Short URL:</strong>
          <a :href="result.short_url" target="_blank" rel="noopener">{{ result.short_url }}</a>
        </li>
        <li>
          <strong>SMS:</strong>
          <span :class="result.channels.sms.ok ? 'channel-ok' : 'channel-fail'">
            {{ result.channels.sms.ok ? 'sent' : 'failed' }}
          </span>
          <span v-if="!result.channels.sms.ok && result.channels.sms.reason">
            ({{ result.channels.sms.reason }})
          </span>
        </li>
        <li v-if="form.email">
          <strong>Email:</strong>
          <span :class="result.channels.email.ok ? 'channel-ok' : 'channel-fail'">
            {{ result.channels.email.ok ? 'sent' : 'skipped/failed' }}
          </span>
        </li>
      </ul>
      <button class="btn" @click="onReset">Send another</button>
    </div>
  </div>
</template>

<script>
import service from '../../services.js';

export default {
  name: 'SendOnboardingLink',
  data() {
    return {
      profiles: [],
      form: {
        profile_id: '',
        phone: '',
        email: '',
        first_name: '',
        last_name: '',
      },
      sending: false,
      error: null,
      result: null,
      qrUrl: null,
      qrLoading: false,
      qrError: null,
    };
  },
  computed: {
    selectedProfile() {
      return this.profiles.find(p => p.id === this.form.profile_id) || null;
    },
    // Skill names the API resolves (skill_default_names); fall back to the
    // raw skill_defaults array if names weren't resolved.
    selectedSkills() {
      const p = this.selectedProfile;
      if (!p) return '';
      if (Array.isArray(p.skill_default_names) && p.skill_default_names.length) {
        return p.skill_default_names.join(', ');
      }
      if (Array.isArray(p.skill_defaults) && p.skill_defaults.length) {
        return p.skill_defaults.join(', ');
      }
      return '';
    },
    // The public launch URL the QR encodes: the persisted cs3.me short URL
    // when minted, else the interim /x/{short_code} page on this host.
    launchUrl() {
      const p = this.selectedProfile;
      if (!p) return null;
      if (p.cs3me_short_url) return p.cs3me_short_url;
      if (p.short_code) return window.location.origin + '/x/' + p.short_code;
      return null;
    },
  },
  watch: {
    'form.profile_id'(id) { this.loadQr(id); },
  },
  mounted() { this.loadProfiles(); },
  methods: {
    formatTime(iso) {
      if (!iso) return '';
      try { return new Date(iso).toLocaleString(); }
      catch (_) { return iso; }
    },
    async loadProfiles() {
      try {
        const res = await service.getUrl('/api/v3/onboarding/my-profiles');
        this.profiles = (res?.data?.data ?? []).filter(p => p.active);
        const dflt = this.profiles.find(p => p.is_default);
        if (dflt && !this.form.profile_id) {
          this.form.profile_id = dflt.id; // triggers the profile_id watcher → loadQr
        }
      } catch (e) {
        this.error = 'Failed to load profiles: ' + (e?.message || 'unknown');
      }
    },
    // Fetch the QR code (base64 PNG data URI) for the selected profile.
    // Stateless endpoint — the QR always reflects the current short_code.
    async loadQr(id) {
      this.qrUrl = null; this.qrError = null;
      if (!id) return;
      this.qrLoading = true;
      try {
        const res = await service.postUrl('/api/v3/onboarding/profiles/' + id + '/qr/refresh', {});
        this.qrUrl = (res && res.data && res.data.data && res.data.data.qr_url) || null;
        if (!this.qrUrl) this.qrError = 'QR unavailable for this profile.';
      } catch (e) {
        this.qrError = e?.response?.data?.error || 'Could not load QR code.';
      } finally {
        this.qrLoading = false;
      }
    },
    async onSubmit() {
      this.sending = true; this.error = null;
      try {
        const payload = {
          profile_id: this.form.profile_id,
          phone:      this.form.phone,
          email:      this.form.email || null,
          first_name: this.form.first_name || null,
          last_name:  this.form.last_name  || null,
        };
        const res = await service.postUrl('/api/v3/onboarding/links', payload);
        this.result = res?.data?.data || null;
      } catch (e) {
        const msg = e?.response?.data?.error || e?.response?.data?.errors;
        this.error = typeof msg === 'string' ? msg
          : msg ? Object.values(msg).flat().join('; ')
          : (e?.message || 'Send failed');
      } finally {
        this.sending = false;
      }
    },
    onReset() {
      this.result = null;
      this.form.phone = '';
      this.form.email = '';
      this.form.first_name = '';
      this.form.last_name = '';
    },
  },
};
</script>

<style scoped>
.report-page__sub {
  font-size: 13px; color: #4a5568; margin: 4px 0 16px; max-width: 560px;
}
.send-form { max-width: 440px; }
.send-form label { display: block; margin-bottom: 12px; font-size: 13px; color: #4a5568; }
.send-form input,
.send-form select {
  display: block; width: 100%; padding: 6px 8px; margin-top: 3px;
  border: 1px solid #cbd5e0; border-radius: 3px; font-size: 14px;
}
.btn { padding: 6px 14px; border: 1px solid #cbd5e0; background: #fff; border-radius: 3px; cursor: pointer; }
.btn-primary { background: #3182ce; color: #fff; border-color: #3182ce; }
.btn-primary:disabled { opacity: .55; }
.result-card {
  margin-top: 24px; max-width: 520px;
  background: #f0fff4; border: 1px solid #9ae6b4; border-radius: 4px;
  padding: 16px;
}
.result-card h5 { margin-top: 0; color: #276749; }
.result-card ul { margin: 0 0 12px; padding-left: 18px; font-size: 14px; }
.result-card li { margin-bottom: 4px; }
.channel-ok { color: #276749; font-weight: 600; }
.channel-fail { color: #c53030; font-weight: 600; }
.report-error { color: #c53030; margin-top: 12px; }
code { font-family: SF Mono, Menlo, Consolas, monospace; font-size: 12px; background: #edf2f7; padding: 1px 5px; border-radius: 2px; }

.o-template-summary {
  max-width: 440px; margin: 0 0 16px;
  background: #f7fafc; border: 1px solid #cbd5e0; border-radius: 4px; padding: 12px 14px;
}
.o-template-summary__title { margin: 0 0 4px; font-size: 15px; font-weight: 700; color: #1f2733; }
.o-template-summary__hint { margin: 0 0 8px; font-size: 12px; color: #4a5562; }
.o-template-summary__list { margin: 0; padding-left: 18px; font-size: 13px; color: #2a3340; line-height: 1.6; }
.o-template-qr {
  margin-top: 12px; padding-top: 12px; border-top: 1px dashed #cbd5e0; text-align: center;
}
.o-template-qr__label { font-size: 12px; font-weight: 600; color: #4a5562; margin-bottom: 6px; }
.o-template-qr__img { width: 170px; height: 170px; image-rendering: pixelated; border: 1px solid #e2e8f0; background: #fff; padding: 6px; border-radius: 4px; }
.o-template-qr__msg { font-size: 12px; color: #718096; padding: 24px 0; }
.o-template-qr__url { display: block; margin-top: 8px; font-size: 12px; word-break: break-all; }
</style>
